import React, {
	useCallback,
	useImperativeHandle,
	useState,
	useRef,
	useEffect,
	MutableRefObject,
	CSSProperties,
	forwardRef
} from "react";
import "./Filters.css";
import Dropdown from "../../shared/dropdown/dropdown";
import Polygon from "@arcgis/core/geometry/Polygon";
import SimpleFillSymbol from "@arcgis/core/symbols/SimpleFillSymbol";
import Graphic from "@arcgis/core/Graphic";
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer";
import Extent from "@arcgis/core/geometry/Extent";
import Radius from "../../radius/Radius";

type FiltersHandle = {
	reset: () => void;
	getFiltersSnapshot: () => { [key: string]: Set<string> };
}

interface iProps {
	view: React.MutableRefObject<__esri.MapView>;
	filterByMapExtent: boolean;
	toggleFilterByMapExtent: Function;
	isSmallScreen: boolean;
	setStateFilterGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
	setRadiusFilterGeometry: React.Dispatch<React.SetStateAction<__esri.Polygon | null>>;
	extent: Extent;
	visible: boolean;
	stateOptions: { value: string, label: string }[];
	stateGeometries: MutableRefObject<{ [key: string]: __esri.Geometry }>;
	classIOptions: string[];
	showMap: () => void;
};


const HazmatOptions = [
	{
		label: 'Non-Hazardous Empty',
		value: 'nhe'
	},
	{
		label: 'Non-Hazardous Loaded',
		value: 'nhl'
	},
	{
		label: 'Hazardous Empty',
		value: 'he'
	},
	{
		label: 'Hazardous Loaded',
		value: 'hl'
	}
];

const highlightLayerId: string = 'StateFilterHighlight';
const radiusSketchLayerId: string = "RadiusSketch";

const Filters: React.ForwardRefRenderFunction<FiltersHandle, iProps> = (props, ref) => {
	useImperativeHandle(ref, () => ({
		reset,
		getFiltersSnapshot
	}));

	const {
		setStateFilterGeometry,
		setRadiusFilterGeometry,
		showMap
	} = props;

	const [selectedStates, setSelectedStates] = useState(new Set<string>());
	const [selectedClassIOptions, setSelectedClassIOptions] = useState(new Set<string>());
	const [selected286kOption, setSelected286kOption] = useState<string | null>(null);
	const [selectedHazmatOptions, setSelectedHazmatOptions] = useState(new Set<string>());

	const [radiusCenterPoint, setRadiusCenterPoint] = useState<__esri.Point | undefined>();
	const [radiusSearchText, setRadiusSearchText] = useState('');

	const [classIDefinitionExpression, setClassIDefinitionExpression] = useState('');
	const [_286kDefinitionExpression, set286kDefinitionExpression] = useState('');
	const [hazmatDefinitionExpression, setHazmatDefinitionExpression] = useState('');

	const interchangeLayer: MutableRefObject<__esri.FeatureLayer | undefined> = useRef();
	const highlightLayer: MutableRefObject<__esri.GraphicsLayer> = useRef(
		new GraphicsLayer({
			id: highlightLayerId,
			listMode: 'hide'
		})
	);
	const highlightSymbol: MutableRefObject<__esri.SimpleFillSymbol> = useRef(
		new SimpleFillSymbol({
			style: "none",
			outline: {
				color: '#327B8F',
				width: 2,
				style: 'solid'
			}
		})
	);

	const radiusSketchLayer = useRef<__esri.GraphicsLayer>(
		new GraphicsLayer({
			id: radiusSketchLayerId,
			listMode: 'hide'
		})
	);

	useEffect(() => {
		props.view.current.when(() => {
			if (!props.view.current.map.findLayerById(highlightLayerId)) {
				props.view.current.map.add(highlightLayer.current);
			}

			if (!props.view.current.map.findLayerById(radiusSketchLayerId)) {
				props.view.current.map.add(radiusSketchLayer.current);
			}

			interchangeLayer.current = props.view.current.map.layers
				.find(layer => layer.title === 'Interchange') as __esri.FeatureLayer;
		});
	}, [props.view])

	const onStateCheckboxChange = (evt: any) => {
		const states = new Set(selectedStates);
		if (evt.target.checked) {
			states.add(evt.target.value);
		} else {
			states.delete(evt.target.value);
		}
		setSelectedStates(states);
	}

	const onClassICheckboxChange = (evt: any) => {
		const classIRRs = new Set(selectedClassIOptions);
		if (evt.target.checked) {
			classIRRs.add(evt.target.value);
		} else {
			classIRRs.delete(evt.target.value);
		}
		setSelectedClassIOptions(classIRRs);
	}

	const on286kCompatibleCheckboxChange = (evt: any) => {
		if (evt.target.checked) {
			setSelected286kOption(evt.target.value);
		} else {
			setSelected286kOption(null);
		}
	}

	const onHazmatCheckboxChange = (evt: any) => {
		const hazmatOptions = new Set(selectedHazmatOptions);
		if (evt.target.checked) {
			hazmatOptions.add(evt.target.value);
		} else {
			hazmatOptions.delete(evt.target.value);
		}
		setSelectedHazmatOptions(hazmatOptions);
	}

	const reset = () => {
		clearAllFilters();
		if (props.view?.current) {
			props.view.current.goTo({
				target: props.extent
			});
		}
		if (!props.filterByMapExtent) {
			props.toggleFilterByMapExtent();
		}
	}

	const clearAllFilters = () => {
		clearStateFilters();
		clearClassIFilters();
		clearRadiusFilter();
		clear286kFilters();
		clearHazmatFilters();
	}

	const clearStateFilters = () => {
		setSelectedStates(new Set());
	}

	const clearClassIFilters = () => {
		setSelectedClassIOptions(new Set());
	}

	const clear286kFilters = () => {
		setSelected286kOption(null);
	};

	const clearHazmatFilters = () => {
		setSelectedHazmatOptions(new Set());
	}

	const clearRadiusFilter = () => {
		radiusSketchLayer.current?.removeAll();
		setRadiusCenterPoint(undefined);
		setRadiusFilterGeometry(null);
		setRadiusSearchText('');
	}

	const selectAllClassIFilters = () => {
		setSelectedClassIOptions(new Set(props.classIOptions));
	}

	const generateStatesDefinitionExpression = () => {
		if (!interchangeLayer.current) {
			return;
		}

		if (selectedStates.size > 0) {
			var geometries = Array.from(selectedStates).map(state => props.stateGeometries.current[state]);
			if (geometries.length > 0) {

				var aggregatedPolygon = new Polygon({
					hasM: false,
					hasZ: false,
					spatialReference: geometries.at(0)?.spatialReference
				});

				geometries.forEach(geometry => {
					if (geometry && geometry.type === 'polygon') {
						const polygon = geometry as __esri.Polygon;
						polygon.rings.forEach(ring => {
							aggregatedPolygon.addRing(ring);
						});
					}
				});

				// push filter geom up to parent
				setStateFilterGeometry(aggregatedPolygon);
			}
		} else {
			setStateFilterGeometry(null)
		}
	}
	useEffect(generateStatesDefinitionExpression, [selectedStates, setStateFilterGeometry, props.stateGeometries]);


	const highlightSelectedStates = () => {
		highlightLayer.current.removeAll();
		var graphics = Array.from(selectedStates).map(state => new Graphic({ geometry: props.stateGeometries.current[state], symbol: highlightSymbol.current }));
		highlightLayer.current.addMany(graphics);

	}
	useEffect(highlightSelectedStates, [selectedStates, props.stateGeometries]);

	const generateClassIDefinitionExpression = () => {
		let definitionExpression = '';
		if (selectedClassIOptions.size > 0) {
			const formattedClassIOptions = Array.from(selectedClassIOptions).map(x => `'${x.toLowerCase()}'`).join(',');
			definitionExpression = `LOWER(Class1Code) IN (${formattedClassIOptions})`;
		}
		setClassIDefinitionExpression(definitionExpression);
	}
	useEffect(generateClassIDefinitionExpression, [selectedClassIOptions]);

	const generate286kDefinitionExpression = () => {
		let definitionExpression = '';

		if (selected286kOption) {
			if (selected286kOption === "yes") {
				definitionExpression = `LOWER(f286k_compatible) IN ('${selected286kOption}', 'partially')`;
			} else {
				definitionExpression = `LOWER(f286k_compatible) IN ('${selected286kOption}')`;
			}
		}
		set286kDefinitionExpression(definitionExpression);
	}
	useEffect(generate286kDefinitionExpression, [selected286kOption]);

	const generateHazmatDefinitionExpression = () => {
		const terms: string[] = []
		if (selectedHazmatOptions.has('nhe')) {
			terms.push(`LOWER(non_hazardous_empty) IN ('yes', 'partially')`);
		}
		if (selectedHazmatOptions.has('nhl')) {
			terms.push(`LOWER(non_hazardous_loaded) IN ('yes', 'partially')`);
		}
		if (selectedHazmatOptions.has('he')) {
			terms.push(`LOWER(hazardous_empty) IN ('yes', 'partially')`);
		}
		if (selectedHazmatOptions.has('hl')) {
			terms.push(`LOWER(hazardous_loaded2) IN ('yes', 'partially')`);
		}
		let definitionExpression = '';
		if (terms.length > 0) {
			definitionExpression = terms.join(" AND ");
		}
		setHazmatDefinitionExpression(definitionExpression);
	}
	useEffect(generateHazmatDefinitionExpression, [selectedHazmatOptions]);

	const applyFilters = () => {
		if (interchangeLayer.current) {
			const terms = [];
			if (_286kDefinitionExpression) {
				terms.push(_286kDefinitionExpression);
			}
			if (classIDefinitionExpression) {
				terms.push(classIDefinitionExpression);
			}
			if (hazmatDefinitionExpression) {
				terms.push(hazmatDefinitionExpression);
			}

			const definitionExpression = terms.map(term => `(${term})`).join(" AND ");
			interchangeLayer.current.definitionExpression = definitionExpression;
		}
	}
	useEffect(applyFilters, [
		// statesDefinitionExpression,
		_286kDefinitionExpression,
		classIDefinitionExpression,
		hazmatDefinitionExpression
	]);

	const getFiltersSnapshot = useCallback(() => {
		return {
			state: selectedStates,
			classIRailroad: selectedClassIOptions,
			_286kCompatible: selected286kOption ? new Set([selected286kOption]) : new Set<string>(),
			hazmat: selectedHazmatOptions
		}
	}, [
		selectedStates,
		selectedClassIOptions,
		selected286kOption,
		selectedHazmatOptions
	])

	let filtersStyle: CSSProperties = {};
	let filterWrapStyle: CSSProperties = {
		flexWrap: "wrap",
		flexShrink: "1"
	};
	let filterOverflowStyle: CSSProperties = {
		justifyContent: "flex-end",
		flexGrow: "1",
		flexShrink: "0"
	};
	let stateOptionsStyle: CSSProperties = {};
	if (props.isSmallScreen) {
		filtersStyle = {
			flexDirection: 'column',
			alignItems: 'start',
			padding: '16px 25px',
			height: 'calc(100% - 28px)',
			border: 'none',
			overflowY: 'auto',
		};

		filterWrapStyle = {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '16px'
		};

		filterOverflowStyle = {
			flexDirection: 'column',
			alignItems: 'flex-start',
			gap: '16px'
		};

		stateOptionsStyle = {
			height: 'calc(100vh - 265px)',
		};
	}

	if (!props.visible) {
		filtersStyle.display = 'none';
	}

	return (
		<div className="railcar-storage-filters" style={filtersStyle}>
			<span className="title">Filters:</span>
			<div className="filter-wrapper" style={filterWrapStyle}>
				<Dropdown isSmallScreen={props.isSmallScreen} buttonClass={selectedStates.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='State or Province'>
					<div className="clear-button-container">
						<button onClick={clearStateFilters}>Clear State Filters</button>
					</div>
					<div style={stateOptionsStyle} className="state-options">
						{props.stateOptions.map(option =>
							<div className="filter-option" key={option.value}>
								<input type="checkbox"
									id={`filter-state-${option.value}`}
									onChange={onStateCheckboxChange}
									checked={selectedStates.has(option.value)}
									value={option.value} />
								<label htmlFor={`filter-state-${option.value}`}>{option.label}</label>
							</div>
						)}
					</div>
				</Dropdown>
				<Dropdown isSmallScreen={props.isSmallScreen} buttonClass={selectedClassIOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label='Class I Railroad'>
					<div className="clear-button-container">
						<button onClick={selectAllClassIFilters}>Select All</button>
						<button onClick={clearClassIFilters}>Clear Class I Filters</button>
					</div>
					<div className="class1-options">
						{props.classIOptions.map(option =>
							<div className="filter-option" key={option}>
								<input type="checkbox"
									id={`filter-class1-${option}`}
									onChange={onClassICheckboxChange}
									checked={selectedClassIOptions.has(option)}
									value={option} />
								<label htmlFor={`filter-class1-${option}`}>{option}</label>
							</div>
						)}
					</div>
				</Dropdown>

				<Dropdown
					isSmallScreen={props.isSmallScreen}
					buttonClass={radiusCenterPoint ? 'bg-digital-blue no-border' : ''}
					label='Distance'>
					<Radius mapView={props.view}
						sketchLayer={radiusSketchLayer}
						highlightSymbol={highlightSymbol}
						centerPoint={radiusCenterPoint}
						setCenterPoint={setRadiusCenterPoint}
						setRadiusGeometry={setRadiusFilterGeometry}
						searchText={radiusSearchText}
						setSearchText={setRadiusSearchText}
						showMap={showMap}
					/>
				</Dropdown>

				<Dropdown isSmallScreen={props.isSmallScreen} buttonClass={selected286kOption ? 'bg-digital-blue no-border' : ''}
					label="286K Compatible">
					<div className="clear-button-container">
						<button onClick={clear286kFilters}>Clear 286K Filter</button>
					</div>
					<div className="_286k-options">
						<div className="filter-option" key="yes">
							<input type="checkbox"
								id="filter-286k-yes"
								name="286kCheckbox"
								onChange={on286kCompatibleCheckboxChange}
								checked={selected286kOption === "yes"}
								value="yes" />
							<label className="capitalize" htmlFor="filter-286k-yes">Compatible</label>
						</div>
					</div>
				</Dropdown>
				<Dropdown isSmallScreen={props.isSmallScreen} buttonClass={
					selectedHazmatOptions.size > 0 ? 'bg-digital-blue no-border' : ''}
					label="Hazmat">

					<div className="clear-button-container">
						<button onClick={clearHazmatFilters}>Clear Hazmat Filters</button>
					</div>
					<div className="hazmat-container">
						<div className="hazmat-options">
							{HazmatOptions.map((option, index) =>
								<div className="filter-option" key={index}>
									<input type="checkbox"
										id={`filter-hazmat-${option.value}`}
										onChange={onHazmatCheckboxChange}
										checked={selectedHazmatOptions.has(option.value)}
										value={option.value} />
									<label htmlFor={`filter-hazmat-${option.value}`}>{option.label}</label>
								</div>
							)}
						</div>
					</div>
				</Dropdown>
			</div>
			<div className="filter-wrapper" style={filterOverflowStyle}>
				<button className="bg-orange no-border" onClick={clearAllFilters}>Clear All Filters</button>
				<div className="checkbox-container">
					<input type="checkbox" id="filter-by-map-extent-checkbox"
						checked={props.filterByMapExtent}
						onChange={() => { props.toggleFilterByMapExtent() }} />
					<label htmlFor="filter-by-map-extent-checkbox">Filter by Map Extent</label>
				</div>
				<button className="bg-gray-6 no-border" onClick={reset}>Reset</button>
			</div>
		</div>
	)
}

export default forwardRef(Filters);